import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import { createPagedSearchAdapterByMetaData } from "commons/ultilities/dynamic-form/auto-complete/legacy-to-paged-adapter";
import {
  resolveAutoCompletePageSize,
  resolveAutoCompleteRenderMode,
} from "commons/ultilities/dynamic-form/auto-complete/mode-resolver";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import {
  ChangeEvent,
  HTMLInputTypeAttribute,
  SyntheticEvent,
  useCallback,
  useMemo,
} from "react";
import { NumericFormat } from "react-number-format";
import { XAutoComplete } from "share-components/mui-wrap/auto-complete/x-auto-complete";
import { XAutoCompleteWithPagination } from "share-components/mui-wrap/auto-complete/x-auto-complete-with-pagination";
import { XTextField } from "../../../../share-components/mui-wrap/text-field/x-text-field";
import { IControl, InputType, OptionItem } from "../models/control";

/**
 * Props interface for the DynamicControl component
 */
type DynamicControlProps = {
  /** The form control to render */
  control: IControl;
  /** Optional callback fired when control value changes */
  onValueChanged?: (control: IControl) => void;
};

/**
 * Main dynamic control component that renders different input types based on control configuration
 * Acts as a router/factory for specific control implementations
 * Uses Material-UI Grid system for responsive layout
 *
 * @param control - The form control configuration and state
 * @param onValueChanged - Optional callback for value change events
 * @returns JSX element containing the appropriate control type
 */
export const DynamicControl = observer(
  ({ control, onValueChanged }: DynamicControlProps) => {
    return (
      control.type && (
        <Grid
          data-type={"control"}
          item
          xs={control.gridSize}
          sx={{ position: "relative" }}
        >
          {control.helpText && (
            <Tooltip title={control.helpText} arrow placement="top">
              <InfoOutlinedIcon
                fontSize="small"
                color="action"
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 8,
                  transform: "translateY(50%)",
                  zIndex: 1,
                  cursor: "help",
                }}
              />
            </Tooltip>
          )}

          {/* Text and Password inputs */}
          {[InputType.text, InputType.password].includes(control.type) && (
            <TextFieldControl control={control}></TextFieldControl>
          )}

          {/* Numeric input with formatting */}
          {control.type === InputType.number && (
            <TextFieldNumberControl
              control={control}
              onValueChanged={onValueChanged}
            ></TextFieldNumberControl>
          )}

          {/* Multi-line text area */}
          {control.type === InputType.textArea && (
            <TextAreaControl control={control}></TextAreaControl>
          )}

          {/* Dropdown select */}
          {control.type === InputType.select && (
            <SelectControl
              control={control}
              onValueChanged={onValueChanged}
            ></SelectControl>
          )}

          {/* Boolean checkbox */}
          {control.type === InputType.checkbox && (
            <CheckboxControl
              control={control}
              onValueChanged={onValueChanged}
            ></CheckboxControl>
          )}

          {/* Multi-select autocomplete */}
          {control.type === InputType.autoCompleteMultiple && (
            <AutoCompleteMultipleControl
              control={control}
            ></AutoCompleteMultipleControl>
          )}

          {/* Single-select autocomplete */}
          {control.type === InputType.autoComplete && (
            <AutoCompleteControl
              control={control}
              onValueChanged={onValueChanged}
            ></AutoCompleteControl>
          )}

          {/* Date picker */}
          {control.type === InputType.datePicker && (
            <DatePickerControl
              control={control}
              onValueChanged={onValueChanged}
            ></DatePickerControl>
          )}

          {/* Read-only label display */}
          {control.type === InputType.label && (
            <LabelControl control={control}></LabelControl>
          )}

          {/* Radio button group */}
          {control.type === InputType.radioGroup && (
            <RadioGroupControl control={control}></RadioGroupControl>
          )}
        </Grid>
      )
    );
  },
);

/**
 * Multi-select autocomplete control implementation
 * Allows selection of multiple options with chip display
 */
const AutoCompleteMultipleControl = observer(
  ({ control }: DynamicControlProps) => {
    /**
     * Handles autocomplete value changes
     * Extracts values from selected option items and updates control
     */
    const autoCompleteMultipleChange = useCallback(
      (
        event: SyntheticEvent,
        value: OptionItem[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<OptionItem> | undefined,
      ) => {
        control.setValueAndValidate(value.map((x) => x.value));
      },
      [control],
    );

    /**
     * Memoized selected values based on current control value
     * Filters option items to show only selected ones
     */
    const selectedValues = useMemo(() => {
      if (control.value) {
        return control.optionItems.filter((x) =>
          control.value.includes(x.value),
        );
      }
      return [];
    }, [control.optionItems, control.value]);

    return (
      <Autocomplete
        multiple
        disabled={control.disabled}
        options={control.optionItems}
        getOptionLabel={(option: OptionItem) => option.label}
        renderOption={(props, option: OptionItem, state) => (
          <li {...props} key={option.value}>
            {option.renderOption ? option.renderOption : option.label}
          </li>
        )}
        value={selectedValues}
        onChange={autoCompleteMultipleChange}
        ChipProps={{ size: "small" }}
        renderInput={
          !control.isValid
            ? ({ ...params }) => (
                <XTextField
                  {...params}
                  variant="outlined"
                  label={control.label}
                  placeholder={control.placeholder}
                  error={true}
                  helperText={control.validationError?.message || ""}
                />
              )
            : ({ ...params }) => (
                <XTextField
                  {...params}
                  variant="outlined"
                  label={control.label}
                  placeholder={control.placeholder}
                />
              )
        }
      />
    );
  },
);

/**
 * Single-select autocomplete control implementation
 * Provides search functionality and async option loading
 */
const AutoCompleteControl = observer(
  ({ control, onValueChanged }: DynamicControlProps) => {
    const autoCompleteRenderMode = resolveAutoCompleteRenderMode(
      control.metaData,
    );

    /**
     * Handles autocomplete value selection
     * Updates control value and triggers callback
     */
    const onValueChange = useCallback(
      (value: OptionItem | null) => {
        const val = value?.value;
        control.setValueAndValidate(val);
        onValueChanged?.(control);
      },
      [control, onValueChanged],
    );

    const pagedSearchFunc = useMemo(
      () => createPagedSearchAdapterByMetaData(control.metaData),
      [control.metaData],
    );

    const pageSize = resolveAutoCompletePageSize(
      control.metaData?.autoCompletePageSize,
    );

    if (autoCompleteRenderMode === "paged") {
      return (
        <XAutoCompleteWithPagination
          label={control.label}
          onValueChanged={onValueChange}
          isValid={control.isValid}
          helpText={control.validationError?.message || ""}
          placeholder={control.placeholder}
          searchFunc={pagedSearchFunc}
          value={control.value}
          defaultOptions={control.optionItems}
          searchOnFocus={!control.metaData?.disabledSearchOnFocus}
          searchWhenRender={control.metaData?.enableSearchWhenRender ?? false}
          pageSize={pageSize}
          resolveSelectedOptionAsync={
            control.metaData?.resolveSelectedOptionAsync
          }
        ></XAutoCompleteWithPagination>
      );
    }

    return (
      <XAutoComplete
        label={control.label}
        onValueChanged={onValueChange}
        isValid={control.isValid}
        helpText={control.validationError?.message || ""}
        placeholder={control.placeholder}
        searchFunc={control.metaData?.searchOptionItemsAsync}
        value={control.value}
        defaultOptions={control.optionItems}
        searchOnFocus={!control.metaData?.disabledSearchOnFocus}
        searchWhenRender={control.metaData?.enableSearchWhenRender ?? false}
        resolveSelectedOptionAsync={
          control.metaData?.resolveSelectedOptionAsync
        }
      ></XAutoComplete>
    );
  },
);

/**
 * Standard text input control implementation
 * Handles text and password input types
 */
const TextFieldControl = observer(({ control }: DynamicControlProps) => {
  /**
   * Handles text input changes
   * Updates control value with validation
   */
  const inputChanged = useCallback(
    async (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      control: IControl,
    ) => {
      control.setValueAndValidate(e.target.value);
    },
    [],
  );

  return (
    <XTextField
      value={control.value}
      disabled={control.disabled}
      fullWidth
      type={control.type as HTMLInputTypeAttribute}
      label={control.label}
      placeholder={control.placeholder}
      error={!control.isValid}
      helperText={
        control.validationError ? control.validationError.message : ""
      }
      onChange={(e) => {
        inputChanged(e, control);
      }}
    ></XTextField>
  );
});

/**
 * Checkbox control implementation
 * Handles boolean value input
 */
const CheckboxControl = observer(
  ({ control, onValueChanged }: DynamicControlProps) => {
    /**
     * Handles checkbox state changes
     * Updates control value and triggers callback
     */
    const onCheckChange = useCallback(
      (event: React.ChangeEvent, checked: boolean) => {
        control.setValueAndValidate(checked);
        onValueChanged?.(control);
      },
      [control, onValueChanged],
    );

    return (
      <Box
        sx={{
          width: "100%",
          paddingLeft: "10px",
          paddingRight: "10px",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              disabled={control.disabled}
              checked={control.value}
              onChange={onCheckChange}
            />
          }
          label={control.label}
        />
      </Box>
    );
  },
);

/**
 * Dropdown select control implementation
 * Renders options as menu items
 */
const SelectControl = observer(
  ({ control, onValueChanged }: DynamicControlProps) => {
    return (
      <XTextField
        fullWidth
        select
        disabled={control.disabled}
        label={control.label}
        value={control.value}
        error={!control.isValid}
        helperText={
          control.validationError ? control.validationError.message : ""
        }
        onChange={(e) => {
          control.setValueAndValidate(e.target.value);
          onValueChanged?.(control);
        }}
      >
        {control.optionItems ? (
          control.optionItems.map((x) => (
            <MenuItem key={x.value} value={x.value}>
              {x.label}
            </MenuItem>
          ))
        ) : (
          <MenuItem></MenuItem>
        )}
      </XTextField>
    );
  },
);

/**
 * Multi-line text area control implementation
 * Configurable number of rows via metadata
 */
const TextAreaControl = observer(({ control }: DynamicControlProps) => {
  /**
   * Handles textarea input changes
   * Updates control value with validation
   */
  const inputChanged = useCallback(
    async (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      control: IControl,
    ) => {
      control.setValueAndValidate(e.target.value);
    },
    [],
  );

  return (
    <XTextField
      value={control.value}
      disabled={control.disabled}
      fullWidth
      multiline
      type={control.type as HTMLInputTypeAttribute}
      label={control.label}
      placeholder={control.placeholder}
      error={!control.isValid}
      rows={control.metaData?.rows || 3}
      helperText={
        control.validationError ? control.validationError.message : ""
      }
      onChange={(e) => {
        inputChanged(e, control);
      }}
    ></XTextField>
  );
});

/**
 * Date picker control implementation
 * Uses dayjs for date handling and Material-UI DatePicker
 */
const DatePickerControl = observer(
  ({ control, onValueChanged }: DynamicControlProps) => {
    /**
     * Memoized date value conversion
     * Converts control value to dayjs object for DatePicker
     */
    const valueLocal = useMemo(() => {
      return control.value ? dayjs(control.value) : null;
    }, [control.value]);

    return (
      <DatePicker
        value={valueLocal}
        label={control.label}
        onChange={(newValue) => {
          control.setValueAndValidate(newValue);
          onValueChanged?.(control);
        }}
        renderInput={
          control.isValid
            ? ({ ...params }) => <XTextField {...params} fullWidth />
            : ({ ...params }) => (
                <XTextField
                  {...params}
                  fullWidth
                  error={true}
                  helperText={
                    control.validationError
                      ? control.validationError.message
                      : "test"
                  }
                />
              )
        }
        disabled={control.disabled}
      />
    );
  },
);

/**
 * Numeric input control implementation
 * Uses react-number-format for formatting and validation
 */
const TextFieldNumberControl = observer(
  ({ control, onValueChanged }: DynamicControlProps) => {
    /**
     * Handles numeric input changes
     * Updates control value with parsed number
     */
    const inputChanged = useCallback(
      async (value: number | null, control: IControl) => {
        control.setValueAndValidate(value);
        onValueChanged?.(control);
      },
      [onValueChanged],
    );

    return (
      <NumericFormat
        fullWidth
        value={control.value ?? ""}
        disabled={control.disabled}
        label={control.label}
        placeholder={control.placeholder}
        displayType="input"
        onValueChange={(values, sourceInfo) => {
          inputChanged(
            values?.floatValue == null ? null : values.floatValue,
            control,
          );
        }}
        error={!control.isValid}
        helperText={
          control.validationError ? control.validationError.message : ""
        }
        customInput={XTextField}
        thousandSeparator
        fixedDecimalScale={control.metaData?.fixDecimalScale != null}
        decimalScale={control.metaData?.fixDecimalScale}
      />
    );
  },
);

/**
 * Read-only label control implementation
 * Displays label and value without input capability
 */
const LabelControl = observer(({ control }: DynamicControlProps) => {
  return (
    <Stack direction={"row"}>
      <Typography>{control.label}</Typography>
      <Typography
        marginLeft={"10px"}
        fontWeight={(theme) => theme.typography.fontWeightMedium}
      >
        {control.value}
      </Typography>
    </Stack>
  );
});

/**
 * Radio button group control implementation
 * Renders multiple radio options with single selection
 */
const RadioGroupControl = observer(({ control }: DynamicControlProps) => {
  /**
   * Handles radio button selection changes
   * Updates control value with selected option
   */
  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      control.setValueAndValidate((event.target as HTMLInputElement).value);
    },
    [control],
  );

  return (
    <FormControl>
      <FormLabel>{control.label}</FormLabel>
      <RadioGroup
        defaultValue={control.defaultValue}
        onChange={handleRadioChange}
      >
        {control.optionItems.map((x) => (
          <FormControlLabel
            key={x.value}
            sx={{ marginLeft: "15px" }}
            value={x.value}
            control={<Radio />}
            label={x.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
});
